import {
    createContext,
    useContext,
    useState,
    useMemo,
  } from "react";
  import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
  import { getRequest, postRequest } from "services/api";
  import { buildQueryString } from "utils/queryHelpers";
  import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
  import Swal from "sweetalert2";
  
  const CategoryContext = createContext();
  
  export const CategoryProvider = ({ children }) => {
    const queryClient = useQueryClient();
  
    const [filterOptions, setFilterOptions] = useState({
      search: "",
      currentPage: 1,
      totalPages: 1,
    });
  
    const queryKey = useMemo(() => ["categories", filterOptions.search], [filterOptions.search]);
  
    const {
      data: response,
      isLoading,
      isError,
      error,
      refetch,
    } = useQuery({
      queryKey,
      queryFn: async () => {
        const query = buildQueryString({ search: filterOptions.search || "" });
        const res = await getRequest(`/category${query}`);
        if (!res.success) {
          throw new Error(res.message || "Failed to fetch categories");
        }
        return res;
      },
      keepPreviousData: true,
    });
  
    const addCategoryMutation = useMutation({
      mutationFn: async (newCategory) => {
        return await postRequest(`/category`, { name: newCategory });
      },
      onSuccess: () => {
        Swal.fire("Success", "Category added successfully!", "success");
        queryClient.invalidateQueries({ queryKey: ["categories"] });
      },
      onError: (err) => {
        Swal.fire("Error", err.message || "Failed to add category", "error");
      },
    });
  
    const handleSearch = (e) => {
      if (e.key === "Enter") {
        setFilterOptions((prevState) => ({
          ...prevState,
          search: e.target.value,
          currentPage: 1,
        }));
      }
    };
  
    const handlePageChange = (page) => {
      setFilterOptions((prevState) => ({
        ...prevState,
        currentPage: page,
      }));
    };
  
    const totalPages = response?.pagination?.last_page || 1;
  
    const Pagination = usePaginationWithComponent({
      totalPages: totalPages,
      currentPage: filterOptions.currentPage,
      onPageChange: handlePageChange,
      visiblePages: 5,
    });
  
    return (
      <CategoryContext.Provider
        value={{
          categories: response?.data || [],
          isLoading,
          isError,
          error,
          filterOptions,
          handleSearch,
          refetch,
          Pagination,
          addCategory: addCategoryMutation.mutate,
        }}
      >
        {children}
      </CategoryContext.Provider>
    );
  };
  
  export const useCategory = () => {
    const context = useContext(CategoryContext);
    if (!context) {
      throw new Error("useCategory must be used within CategoryProvider");
    }
  
    const fetchOptions = async (inputValue) => {
      const response = await getRequest(`/category?search=${inputValue}`);
      if (response.success) {
        return response.data.map((category) => ({
          value: category.id,
          label: category.name,
        }));
      }
      return [];
    };
  
    return { ...context, fetchOptions };
  };
  